import React, { useState } from 'react';
import {
  LineaPedido3D, ResultadoLinea3D, PerfilFilamento, ExtraLinea3D,
} from '../../types';
import { formatearImporte, MATERIALES_3D } from '../../utils/calculo3d';
import { Campo, NumInput, Segmentado, claseInput } from './campos';
import { ChevronDown, Trash2, Plus, Copy, Layers } from 'lucide-react';

interface Props {
  linea: LineaPedido3D;
  resultado?: ResultadoLinea3D;
  indice: number;
  total: number;
  moneda: string;
  modo: 'margen' | 'precio';
  multiplicadorPedido: number;
  perfilesFilamento: PerfilFilamento[];
  onChange: (campo: keyof LineaPedido3D, valor: any) => void;
  onDuplicar: () => void;
  onEliminar: () => void;
  onGuardarPerfil: () => void;
  abiertaPorDefecto?: boolean;
}

export const LineaCard3D: React.FC<Props> = ({
  linea, resultado, indice, total, moneda, modo, multiplicadorPedido,
  perfilesFilamento, onChange, onDuplicar, onEliminar, onGuardarPerfil,
  abiertaPorDefecto = false,
}) => {
  const [abierta, setAbierta] = useState(abiertaPorDefecto);
  const [perfilSel, setPerfilSel] = useState('');

  const f = (v: number) => formatearImporte(v, moneda);

  const cargarPerfil = (id: string) => {
    setPerfilSel(id);
    const perfil = perfilesFilamento.find(x => x.id === id);
    if (!perfil) return;
    onChange('material', perfil.material);
    onChange('precioRollo', perfil.precioRollo);
    onChange('pesoRollo', perfil.pesoRollo);
    onChange('merma', perfil.merma);
    onChange('cambiosColor', perfil.cambiosColor);
    onChange('purgaPorCambio', perfil.purgaPorCambio);
  };

  const setExtra = (i: number, campo: keyof ExtraLinea3D, valor: any) =>
    onChange('extras', linea.extras.map((x, j) => (j === i ? { ...x, [campo]: valor } : x)));
  const quitarExtra = (i: number) =>
    onChange('extras', linea.extras.filter((_, j) => j !== i));
  const anadirExtra = () =>
    onChange('extras', [...linea.extras, { nombre: '', importe: 0 }]);

  const usaMultPropio = linea.multiplicador !== undefined && linea.multiplicador !== null;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 mb-3 overflow-hidden">

      {/* ── Cabecera / resumen ─────────────────────────────────────────────── */}
      <div className="flex items-center gap-2 px-3 py-2.5">
        <button type="button" onClick={() => setAbierta(v => !v)} aria-expanded={abierta}
          className="flex items-center gap-2.5 flex-1 min-w-0 text-left group">
          <span className="w-6 h-6 shrink-0 rounded-md bg-orange-100 dark:bg-orange-900/40
                           text-orange-700 dark:text-orange-400 text-[11px] font-bold
                           flex items-center justify-center">
            {indice + 1}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block font-semibold text-sm text-gray-900 dark:text-white truncate">
              {linea.nombre || `Artículo ${indice + 1}`}
            </span>
            <span className="block text-[11px] text-gray-500 dark:text-gray-400 truncate">
              {linea.unidades} ud{linea.unidades === 1 ? '' : 's'} · {linea.material}
              {resultado && ` · ${resultado.platos} plato${resultado.platos === 1 ? '' : 's'} · ${resultado.gramos.toFixed(0)} g`}
            </span>
          </span>
          <ChevronDown className={`w-4 h-4 text-gray-400 shrink-0 transition-transform ${abierta ? 'rotate-180' : ''}`} />
        </button>

        {resultado && (
          <div className="hidden sm:block text-right shrink-0 pl-2">
            <p className="font-mono text-sm tabular-nums text-gray-900 dark:text-white">
              {f(resultado.precioUnidad)}<span className="text-gray-400 text-[11px]">/ud</span>
            </p>
            <p className="font-mono text-[11px] tabular-nums text-gray-500 dark:text-gray-400">
              coste {f(resultado.costeUnidad)}
            </p>
          </div>
        )}

        <div className="flex items-center gap-0.5 shrink-0">
          <button type="button" onClick={onDuplicar} title="Duplicar artículo"
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition">
            <Copy className="w-4 h-4" />
          </button>
          <button type="button" onClick={onEliminar} disabled={total <= 1}
            title={total <= 1 ? 'Un pedido necesita al menos un artículo' : 'Quitar artículo'}
            className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition
                       disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:text-gray-400 disabled:hover:bg-transparent">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* ── Detalle ────────────────────────────────────────────────────────── */}
      {abierta && (
        <div className="px-4 pb-4 pt-3 border-t border-gray-100 dark:border-gray-700 space-y-4">

          <div className="grid gap-3 sm:grid-cols-2">
            <Campo label="Nombre del artículo">
              <input type="text" value={linea.nombre} placeholder="Jarrón alto"
                onChange={e => onChange('nombre', e.target.value)} className={claseInput} />
            </Campo>
            <Campo label="Unidades de este artículo">
              <NumInput valor={linea.unidades} onChange={v => onChange('unidades', v)} unidad="uds" min={1} />
            </Campo>
          </div>

          {/* Geometría y platos */}
          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-gray-400 mb-2">Datos del slicer</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <Campo label="Los datos son de…">
                <Segmentado valor={linea.base} onChange={v => onChange('base', v)}
                  opciones={[{ valor: 'pieza', label: 'Una pieza' }, { valor: 'plato', label: 'Un plato' }]} />
              </Campo>
              <Campo label="Piezas por plato"
                ayuda="Cuántas caben a la vez. Decide cuántos platos hay que lanzar.">
                <NumInput valor={linea.piezasPorPlato} onChange={v => onChange('piezasPorPlato', v)} unidad="uds" min={1} />
              </Campo>
            </div>
            <div className="grid gap-3 sm:grid-cols-3 mt-3">
              <Campo label="Filamento usado">
                <NumInput valor={linea.gramos} onChange={v => onChange('gramos', v)} unidad="g" step={0.1} />
              </Campo>
              <Campo label="Tiempo · horas">
                <NumInput valor={linea.horas} onChange={v => onChange('horas', v)} unidad="h" />
              </Campo>
              <Campo label="Tiempo · minutos">
                <NumInput valor={linea.minutos} onChange={v => onChange('minutos', v)} unidad="min" max={59} />
              </Campo>
            </div>
            {resultado && (
              <p className="flex items-start gap-1.5 text-[11px] text-gray-500 dark:text-gray-500 mt-2">
                <Layers className="w-3.5 h-3.5 shrink-0 mt-px" />
                {resultado.platos} plato{resultado.platos === 1 ? '' : 's'} para {linea.unidades} ud
                {linea.unidades === 1 ? '' : 's'}, contando las reimpresiones por fallo.
                {linea.cambiosColor > 0 && ' La purga de los cambios de color se paga por plato, aunque vaya a medias.'}
              </p>
            )}
          </div>

          {/* Filamento */}
          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-gray-400 mb-2">Filamento</p>
            <div className="flex flex-wrap gap-2 items-center mb-3">
              <select value={perfilSel} onChange={e => cargarPerfil(e.target.value)}
                className={`${claseInput} flex-1 min-w-[150px]`}>
                <option value="">— Cargar perfil —</option>
                {perfilesFilamento.map(x => <option key={x.id} value={x.id}>{x.nombre}</option>)}
              </select>
              <button type="button" onClick={onGuardarPerfil}
                className="px-3 py-2 text-xs font-semibold rounded-lg border border-gray-300 dark:border-gray-600
                           text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition">
                Guardar como perfil
              </button>
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <Campo label="Material">
                <select value={linea.material} onChange={e => onChange('material', e.target.value)} className={claseInput}>
                  {MATERIALES_3D.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </Campo>
              <Campo label="Precio del rollo">
                <NumInput valor={linea.precioRollo} onChange={v => onChange('precioRollo', v)} unidad={moneda} step={0.01} />
              </Campo>
              <Campo label="Peso del rollo">
                <NumInput valor={linea.pesoRollo} onChange={v => onChange('pesoRollo', v)} unidad="g" min={1} />
              </Campo>
            </div>
            <div className="grid gap-3 sm:grid-cols-3 mt-3">
              <Campo label="Purga, soportes y merma"
                ayuda="Un color, sin soportes: 2–5 %. Con soportes: 10–15 %.">
                <NumInput valor={linea.merma} onChange={v => onChange('merma', v)} unidad="%" max={100} step={0.5} />
              </Campo>
              <Campo label="Cambios de color por plato"
                ayuda="Solo AMS / multicolor. Déjalo en 0 si imprimes a un color.">
                <NumInput valor={linea.cambiosColor} onChange={v => onChange('cambiosColor', v)} unidad="×" />
              </Campo>
              <Campo label="Purga por cambio" ayuda="Bambu A1 con AMS Lite: 3–6 g por cambio.">
                <NumInput valor={linea.purgaPorCambio} onChange={v => onChange('purgaPorCambio', v)} unidad="g" step={0.1} />
              </Campo>
            </div>
          </div>

          {/* Acabado y extras */}
          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-gray-400 mb-2">Acabado y extras</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <Campo label="Post-procesado por unidad"
                ayuda="Quitar soportes, lijar, revisar y embalar cada pieza.">
                <NumInput valor={linea.minPostproceso} onChange={v => onChange('minPostproceso', v)} unidad="min" />
              </Campo>
              {modo === 'margen' ? (
                <Campo label="Margen"
                  ayuda={usaMultPropio
                    ? `Precio = coste × multiplicador. Este artículo va por libre; con «Usar el general» vuelve al ×${multiplicadorPedido} del pedido.`
                    : `Precio = coste × multiplicador. Ahora hereda el ×${multiplicadorPedido} del pedido; cámbialo si este artículo debe llevar otro margen.`}>
                  <div className="flex gap-2">
                    <NumInput valor={usaMultPropio ? (linea.multiplicador as number) : multiplicadorPedido}
                      onChange={v => onChange('multiplicador', v)} unidad="×" min={0} step={0.1} />
                    {usaMultPropio && (
                      <button type="button" onClick={() => onChange('multiplicador', undefined)}
                        className="px-3 py-2 text-xs font-semibold rounded-lg border border-gray-300 dark:border-gray-600
                                   text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition whitespace-nowrap">
                        Usar el general
                      </button>
                    )}
                  </div>
                </Campo>
              ) : (
                <Campo label="Precio objetivo por unidad" ayuda="Sin IVA. Te dice qué margen te queda.">
                  <NumInput valor={linea.precioObjetivo} onChange={v => onChange('precioObjetivo', v)}
                    unidad={moneda} step={0.5} />
                </Campo>
              )}
            </div>

            <div className="mt-3 space-y-2">
              {linea.extras.map((x, i) => (
                <div key={i} className="grid gap-2 grid-cols-[1fr_120px_auto] items-center">
                  <input type="text" value={x.nombre} placeholder="Anilla, pintura, imán…"
                    onChange={e => setExtra(i, 'nombre', e.target.value)} className={claseInput} />
                  <NumInput valor={x.importe} onChange={v => setExtra(i, 'importe', v)} unidad={moneda} step={0.01} />
                  <button type="button" onClick={() => quitarExtra(i)} title="Quitar"
                    className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-400
                               hover:text-red-600 hover:border-red-400 transition">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
            <button type="button" onClick={anadirExtra}
              className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg
                         border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300
                         hover:bg-gray-50 dark:hover:bg-gray-700 transition">
              <Plus className="w-3.5 h-3.5" /> Añadir extra por unidad
            </button>
          </div>

          {/* Resultado de la línea */}
          {resultado && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-3 border-t border-gray-100 dark:border-gray-700">
              {[
                ['Coste / ud', f(resultado.costeUnidad)],
                ['Precio / ud', f(resultado.precioUnidad)],
                ['Subtotal con IVA', f(resultado.precioTotalConIva)],
                ['Beneficio', f(resultado.beneficioTotal)],
              ].map(([k, v], i) => (
                <div key={k}>
                  <p className="text-[11px] text-gray-500 dark:text-gray-400">{k}</p>
                  <p className={`font-mono text-sm tabular-nums ${
                    i === 3
                      ? resultado.beneficioTotal < 0
                        ? 'text-red-600 dark:text-red-400 font-semibold'
                        : 'text-green-600 dark:text-green-400 font-semibold'
                      : 'text-gray-900 dark:text-white'
                  }`}>{v}</p>
                </div>
              ))}
              <p className="col-span-2 sm:col-span-4 text-[11px] text-gray-500 dark:text-gray-500">
                Incluye {f(resultado.cuotaFija)} de los costes fijos del pedido (diseño, preparación y envío),
                repartidos a prorrata del coste de cada línea.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
